import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { listarFeedbacks, aprovarFeedback, deletarFeedback } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { CheckCircle, XCircle, Trash2, Star } from 'lucide-react';
import { toast } from 'sonner';

const AdminFeedbacks = () => {
  const { token } = useAuth();
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('pendentes'); // pendentes, aprovados, todos

  useEffect(() => {
    carregarFeedbacks();
  }, [filtro]);

  const carregarFeedbacks = async () => {
    try {
      const apenasAprovados = filtro === 'aprovados';
      const data = await listarFeedbacks(apenasAprovados);
      
      let feedbacksFiltrados = data;
      if (filtro === 'pendentes') {
        feedbacksFiltrados = data.filter(f => !f.aprovado);
      }
      
      setFeedbacks(feedbacksFiltrados);
    } catch (error) {
      toast.error('Erro ao carregar feedbacks');
    } finally {
      setLoading(false);
    }
  };

  const handleAprovar = async (id) => {
    try {
      await aprovarFeedback(id, token);
      toast.success('Feedback aprovado!');
      carregarFeedbacks();
    } catch (error) {
      toast.error('Erro ao aprovar feedback');
    }
  };

  const handleDeletar = async (id) => {
    if (!window.confirm('Tem certeza que deseja deletar este feedback?')) return;

    try {
      await deletarFeedback(id, token);
      toast.success('Feedback deletado!');
      carregarFeedbacks();
    } catch (error) {
      toast.error('Erro ao deletar feedback');
    }
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        size={16}
        className={i < rating ? 'star-filled' : 'star-empty'}
        fill={i < rating ? 'currentColor' : 'none'}
      />
    ));
  };

  return (
    <div className="admin-feedbacks">
      <div className="admin-page-header">
        <h2 className="admin-page-title">Gestão de Feedbacks</h2>
        
        <div className="feedback-filters">
          <Button 
            variant={filtro === 'pendentes' ? 'default' : 'outline'}
            onClick={() => setFiltro('pendentes')}
          >
            Pendentes
          </Button>
          <Button 
            variant={filtro === 'aprovados' ? 'default' : 'outline'}
            onClick={() => setFiltro('aprovados')}
          >
            Aprovados
          </Button>
          <Button 
            variant={filtro === 'todos' ? 'default' : 'outline'}
            onClick={() => setFiltro('todos')}
          >
            Todos
          </Button>
        </div>
      </div>

      {loading ? (
        <p>Carregando...</p>
      ) : feedbacks.length === 0 ? (
        <p className="admin-empty">Nenhum feedback encontrado.</p>
      ) : (
        <div className="admin-feedbacks-list">
          {feedbacks.map((feedback) => (
            <Card key={feedback.id} className={`admin-feedback-card ${feedback.aprovado ? 'aprovado' : 'pendente'}`}>
              <CardContent className="admin-feedback-content">
                <div className="feedback-header">
                  <div>
                    <h3>{feedback.nome}</h3>
                    {feedback.email && <p className="feedback-email">{feedback.email}</p>}
                  </div>
                  <div className="feedback-stars">
                    {renderStars(feedback.rating)}
                  </div>
                </div>

                <p className="feedback-comentario">{feedback.comentario}</p>

                <div className="feedback-footer">
                  <span className="feedback-status">
                    {feedback.aprovado ? (
                      <><CheckCircle size={16} color="green" /> Aprovado</>
                    ) : (
                      <><XCircle size={16} color="orange" /> Pendente</>
                    )}
                  </span>
                  <span className="feedback-data">
                    {new Date(feedback.criado_em).toLocaleDateString('pt-PT')}
                  </span>
                </div>

                <div className="admin-feedback-actions">
                  {!feedback.aprovado && (
                    <Button onClick={() => handleAprovar(feedback.id)} size="sm">
                      <CheckCircle size={16} /> Aprovar
                    </Button>
                  )}
                  <Button onClick={() => handleDeletar(feedback.id)} variant="destructive" size="sm">
                    <Trash2 size={16} /> Deletar
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminFeedbacks;
